import { getGuestToken } from "./guest";
import { getScriptarioLocaleFromPath } from "@/config/locales";
import { buildApiUrl, getAudio2TextAttributionSiteId, getTranscriptaAttributionSiteId, isAudio2Text, isScriptario, siteLocale, yandexMetrikaId } from "./config";

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
  }
}

const ATTRIBUTION_HEADER_MAP = {
  utm_source: "x-utm-source",
  utm_medium: "x-utm-medium",
  utm_campaign: "x-utm-campaign",
  utm_term: "x-utm-term",
  utm_content: "x-utm-content",
  gclid: "x-gclid",
  yclid: "x-yclid",
  referral_code: "x-referral-code",
} as const;

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function toAttributionHeaderValue(value: string | null | undefined): string | null {
  if (value == null) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  try {
    return encodeURIComponent(raw);
  } catch {
    return null;
  }
}

async function resolveYmUid(timeoutMs = 1000): Promise<string | null> {
  const cookieYmUid = readCookie("_ym_uid");
  if (cookieYmUid && /^\d{17,19}$/.test(cookieYmUid)) return cookieYmUid;

  if (!yandexMetrikaId || typeof window === "undefined" || typeof window.ym !== "function") return null;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: unknown) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timerId);
      const normalized = typeof value === "string" ? value.trim() : "";
      resolve(/^\d{17,19}$/.test(normalized) ? normalized : null);
    };
    const timerId = window.setTimeout(() => finish(null), timeoutMs);

    try {
      window.ym?.(yandexMetrikaId, "getClientID", finish);
    } catch {
      finish(null);
    }
  });
}

async function addAttributionHeaders(headers: Record<string, string>) {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const values: Partial<Record<keyof typeof ATTRIBUTION_HEADER_MAP, string>> = {};
  let hasUrlUtm = false;
  let hasCookieUtm = false;
  let isReferralSynthetic = false;
  let clidChannel: "url_yclid" | "url_gclid" | null = null;

  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const) {
    const fromUrl = params.get(key);
    if (fromUrl) {
      values[key] = fromUrl;
      hasUrlUtm = true;
      continue;
    }

    const fromCookie = readCookie(key);
    if (fromCookie) {
      values[key] = fromCookie;
      hasCookieUtm = true;
    }
  }

  for (const key of ["gclid", "yclid"] as const) {
    const value = params.get(key) || readCookie(key);
    if (value) values[key] = value;
  }

  const referralCode = params.get("ref") || params.get("referral") || readCookie("referral_code");
  if (referralCode) {
    values.referral_code = referralCode;
    if (!hasUrlUtm && !hasCookieUtm) {
      values.utm_source = "referral";
      values.utm_medium = "invite";
      values.utm_campaign = `ref_${referralCode}`;
      hasUrlUtm = true;
      isReferralSynthetic = true;
    }
  }

  // A guest created from an ad click must never be attributed as direct/organic by the
  // backend. When nothing explicit is present, synthesize utm from the click id.
  if (!hasUrlUtm && !hasCookieUtm && (values.yclid || values.gclid)) {
    if (values.yclid) {
      values.utm_source = "yandex_direct";
      clidChannel = "url_yclid";
    } else {
      values.utm_source = "google_ads";
      clidChannel = "url_gclid";
    }
    values.utm_medium = "cpc";
    // No utm_campaign on purpose — the click id carries the campaign.
  }

  for (const [field, header] of Object.entries(ATTRIBUTION_HEADER_MAP)) {
    const value = toAttributionHeaderValue(values[field as keyof typeof ATTRIBUTION_HEADER_MAP]);
    if (value) headers[header] = value;
  }

  if (hasUrlUtm) {
    headers["x-utm-attribution-channel"] = isReferralSynthetic ? "url_ref_synthetic" : "url_explicit";
  } else if (clidChannel) {
    headers["x-utm-attribution-channel"] = clidChannel;
  } else if (hasCookieUtm) {
    headers["x-utm-attribution-channel"] = "cookie_landing";
  }

  const ymUid = await resolveYmUid();
  if (ymUid) {
    headers["x-ym-uid"] = ymUid;
  }
}

function addAppLocaleHeader(headers: Record<string, string>) {
  if (!isScriptario) return;
  const locale = typeof window === "undefined"
    ? siteLocale
    : getScriptarioLocaleFromPath(window.location.pathname);
  headers["X-App-Locale"] = locale;
}

function addAttributionSiteHeader(headers: Record<string, string>) {
  if (isAudio2Text) headers["X-Site-Id"] = getAudio2TextAttributionSiteId();
  else if (!isScriptario) headers["X-Site-Id"] = getTranscriptaAttributionSiteId();
}

// Длительность — это оптимизация, а не обязательное поле: бэкенд принимает
// duration=1 и считает реальную сам. Поэтому измерение не имеет права
// блокировать загрузку.
const DURATION_PROBE_BUDGET_MS = 5000;

function readDurationWithElement(file: File, tag: "audio" | "video"): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(tag) as HTMLMediaElement;
    let settled = false;

    const finish = (value: number | undefined) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timerId);
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(value) && (value as number) > 0 ? value : undefined);
    };

    // Без него промис висит вечно, если браузер не пришлёт ни loadedmetadata,
    // ни error. Так и происходит на мобильных: iOS игнорирует preload и не
    // трогает медиа без пользовательского жеста, а страница под фоновой
    // вкладкой вовсе не декодирует метаданные.
    const timerId = window.setTimeout(() => finish(undefined), DURATION_PROBE_BUDGET_MS);

    el.preload = "metadata";
    el.addEventListener("loadedmetadata", () => {
      if (settled) return;
      if (Number.isFinite(el.duration) && el.duration > 0) {
        finish(el.duration);
        return;
      }
      // Длительности в метаданных нет — обычное дело для WebM с MediaRecorder.
      // Перемотка за конец заставляет браузер зажать позицию реальной
      // длительностью и поднять durationchange.
      el.addEventListener("durationchange", () => finish(el.duration), { once: true });
      el.currentTime = Number.MAX_SAFE_INTEGER;
    }, { once: true });
    el.addEventListener("error", () => finish(undefined), { once: true });
    el.src = url;
  });
}

export async function getFileDuration(file: File): Promise<number | undefined> {
  const primary = file.type.startsWith("audio/") ? "audio" : "video";
  const fallback = primary === "audio" ? "video" : "audio";

  // Тип из file.type на мобильных врёт: iOS отдаёт пустую строку для части
  // файлов из «Фото», и аудио уезжает пробоваться как видео. Поэтому запасной
  // тег есть, но ждать его отдельные 5 с нельзя — иначе суммарный простой
  // удваивается. Бюджет один на всё измерение; элементы, не успевшие в него,
  // закроются по собственным таймерам и сами отзовут blob-URL.
  const probe = (async () => {
    const measured = await readDurationWithElement(file, primary);
    return measured ?? readDurationWithElement(file, fallback);
  })();

  return Promise.race([
    probe,
    new Promise<undefined>((resolve) => {
      window.setTimeout(() => resolve(undefined), DURATION_PROBE_BUDGET_MS);
    }),
  ]);
}

const MULTIPART_PART_SIZE = 16 * 1024 * 1024; // 16MB
const MULTIPART_CONCURRENCY = 2; // parallel parts in-flight
const MULTIPART_MAX_RETRIES = 3; // per-part retry attempts

// xhr.upload.progress считает байты, ОТДАННЫЕ в сокет, а не принятые сервером.
// На 16-мегабайтных частях разрыв между «отдали» и «сервер подтвердил ETag»
// доходит до десятков секунд, и всё это время бар честно показывал 100%, пока
// ничего видимого не происходило. Поэтому отданные байты стоят 0.9 части, а
// подтверждение — оставшиеся 0.1. Тот же вес и та же причина, что в ЛК
// (a31ddd65, 05.10.2026).
const PART_SENT_WEIGHT = 0.9;

export type UploadStage = "uploading" | "confirming" | "finalizing";
export type UploadProgress = { percent: number; stage: UploadStage };

export function computeUploadProgress(
  sentFractions: readonly number[],
  confirmedParts: readonly boolean[],
  totalParts: number
): UploadProgress {
  if (totalParts <= 0) return { percent: 0, stage: "uploading" };

  let sum = 0;
  let allSent = true;
  let allConfirmed = true;
  for (let i = 0; i < totalParts; i++) {
    const sent = sentFractions[i] ?? 0;
    const confirmed = confirmedParts[i] ?? false;
    sum += sent * PART_SENT_WEIGHT + (confirmed ? 1 - PART_SENT_WEIGHT : 0);
    if (sent < 1) allSent = false;
    if (!confirmed) allConfirmed = false;
  }

  const raw = Math.round((sum / totalParts) * 100);
  // 100% отдаём только когда работа действительно кончилась: иначе бар снова
  // упирается в потолок и замирает.
  const percent = allConfirmed ? raw : Math.min(99, raw);
  return { percent, stage: allSent && !allConfirmed ? "confirming" : "uploading" };
}
export const UPLOAD_LEGAL_CONSENT_VERSION = "2026-09-04";
export const UPLOAD_LEGAL_CONSENT_STORAGE_KEY = "transcripta_upload_legal_consent_2026-09-04";
export const UPLOAD_LEGAL_CONSENT = {
  accepted: true,
  action: "upload_file",
  version: UPLOAD_LEGAL_CONSENT_VERSION,
};

export async function fetchUploadLegalConsentStatus(options: { includeGuestToken?: boolean } = {}): Promise<boolean> {
  const headers: Record<string, string> = { "x-guest-lazy-create": "0" };
  addAppLocaleHeader(headers);
  const guestToken = options.includeGuestToken ? getGuestToken() : null;
  if (guestToken) {
    headers["x-guest-token"] = guestToken;
  }

  const res = await fetch(buildApiUrl("/api/legal-consents/upload"), { headers });
  if (!res.ok) return false;
  const data = await res.json().catch(() => null);
  return !!data?.hasUploadLegalConsent;
}

export async function uploadFile(
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  options: { includeGuestToken?: boolean } = {}
): Promise<{ preUploadId: string; duration?: number }> {
  const duration = await getFileDuration(file);

  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const mediaType = file.type.startsWith('video/') ? 'video' : 'audio';

  // Step 1: Init multipart upload
  const initHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
  addAppLocaleHeader(initHeaders);
  addAttributionSiteHeader(initHeaders);
  const guestToken = options.includeGuestToken ? getGuestToken() : null;
  if (guestToken) {
    initHeaders['x-guest-token'] = guestToken;
    await addAttributionHeaders(initHeaders);
  }

  const initRes = await fetch(buildApiUrl('/api/uploads'), {
    method: 'POST',
    headers: initHeaders,
    body: JSON.stringify({
      filename: file.name,
      mediaType,
      duration: duration != null && duration > 0 ? Math.round(duration) : 1,
      totalBytes: file.size,
      ...(!isScriptario ? { legalConsent: UPLOAD_LEGAL_CONSENT } : {}),
    }),
  });
  if (!initRes.ok) {
    const err = await initRes.json().catch(() => ({ error: 'Init failed' }));
    throw new Error(err.error || err.message || 'Upload init failed');
  }
  const { uploadId, s3UploadId, key: s3Key } = await initRes.json();
  if (!isScriptario) {
    try {
      window.localStorage.setItem(UPLOAD_LEGAL_CONSENT_STORAGE_KEY, "true");
    } catch {
      // Durable consent evidence is stored by the backend.
    }
  }

  // Step 2: Upload parts concurrently (pool of N workers, retry per part)
  const totalParts = Math.ceil(file.size / MULTIPART_PART_SIZE);
  const parts: { PartNumber: number; ETag: string }[] = [];
  const partFractions = new Array<number>(totalParts).fill(0);
  const confirmedParts = new Array<boolean>(totalParts).fill(false);

  const reportProgress = () => {
    onProgress?.(computeUploadProgress(partFractions, confirmedParts, totalParts));
  };

  let nextPart = 1;
  let aborted = false;
  let firstError: Error | null = null;

  const uploadPart = (partNumber: number): Promise<void> => {
    const start = (partNumber - 1) * MULTIPART_PART_SIZE;
    const end = Math.min(start + MULTIPART_PART_SIZE, file.size);
    const partBlob = file.slice(start, end);
    const tokenParam = guestToken ? `&token=${encodeURIComponent(guestToken)}` : "";
    const partUrl = `/s3-upload/${uploadId}/${partNumber}?s3UploadId=${encodeURIComponent(s3UploadId)}&ext=${encodeURIComponent(fileExt)}${tokenParam}&s3Key=${encodeURIComponent(s3Key)}`;
    const partIndex = partNumber - 1;

    const attempt = (): Promise<string> => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", partUrl, true);

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          partFractions[partIndex] = Math.max(partFractions[partIndex], e.loaded / e.total);
          reportProgress();
        }
      });

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = (xhr.getResponseHeader("ETag") || "").replace(/^["']|["']$/g, "");
          // 2xx без ETag — не успех: сборка на /complete такую часть отвергнет.
          // Лучше потратить попытку здесь, чем узнать об этом в самом конце.
          if (!etag) {
            reject(new Error(`Part ${partNumber} responded ${xhr.status} without an ETag`));
            return;
          }
          resolve(etag);
        } else {
          reject(new Error(`Part ${partNumber} upload failed with status ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error(`Part ${partNumber} network error`));
      xhr.ontimeout = () => reject(new Error(`Part ${partNumber} timeout`));
      xhr.timeout = 300000; // 5 min per part
      xhr.send(partBlob);
    });

    return (async () => {
      let lastError: Error | null = null;
      for (let attemptNo = 0; attemptNo < MULTIPART_MAX_RETRIES; attemptNo++) {
        if (aborted) throw firstError || new Error('Upload aborted');
        try {
          const etag = await attempt();
          partFractions[partIndex] = 1;
          confirmedParts[partIndex] = true;
          reportProgress();
          parts.push({ PartNumber: partNumber, ETag: etag });
          return;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          if (attemptNo < MULTIPART_MAX_RETRIES - 1 && !aborted) {
            await new Promise((r) => setTimeout(r, 1000 * (attemptNo + 1)));
          }
        }
      }
      // All retries exhausted — abort the whole upload.
      if (!aborted) {
        aborted = true;
        firstError = lastError || new Error(`Part ${partNumber} failed after ${MULTIPART_MAX_RETRIES} attempts`);
      }
      throw firstError;
    })();
  };

  const worker = async (): Promise<void> => {
    while (true) {
      if (aborted) throw firstError || new Error('Upload aborted');
      const partNumber = nextPart++;
      if (partNumber > totalParts) return;
      await uploadPart(partNumber);
    }
  };

  const workerCount = Math.min(MULTIPART_CONCURRENCY, totalParts);
  const workers: Promise<void>[] = [];
  for (let i = 0; i < workerCount; i++) workers.push(worker());

  try {
    await Promise.all(workers);
  } catch (err) {
    // Ensure any in-flight workers observe the abort flag and reject quickly.
    aborted = true;
    if (!firstError) firstError = err instanceof Error ? err : new Error(String(err));
    throw firstError;
  }

  // S3 requires parts ordered by PartNumber in the complete call.
  parts.sort((a, b) => a.PartNumber - b.PartNumber);

  // S3 склеивает части на своей стороне, и на крупном файле это ещё одна
  // заметная пауза. Без отдельной стадии она выглядела бы как замерший бар.
  onProgress?.({ percent: 99, stage: "finalizing" });

  const completeHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
  addAppLocaleHeader(completeHeaders);
  addAttributionSiteHeader(completeHeaders);
  if (guestToken) {
    completeHeaders['x-guest-token'] = guestToken;
    completeHeaders['x-guest-lazy-create'] = '0';
    await addAttributionHeaders(completeHeaders);
  }

  // Step 3: Complete with preUpload
  const completeRes = await fetch(buildApiUrl(`/api/uploads/${uploadId}/complete`), {
    method: 'POST',
    headers: completeHeaders,
    body: JSON.stringify({
      parts,
      preUpload: true,
      s3Key,
      s3UploadId,
      filename: file.name,
      duration: duration != null && duration > 0 ? Math.round(duration) : 1,
      mediaType,
    }),
  });
  const completeData = await completeRes.json();
  if (!completeRes.ok || !completeData.success) {
    throw new Error(completeData.message || completeData.error || 'Complete failed');
  }

  onProgress?.({ percent: 100, stage: "uploading" });

  return { preUploadId: completeData.preUploadId, duration };
}
